import React, { useCallback, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, InputLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { simplifiedAuthStyles } from '../../styles/simplifiedAuth';
import { PasswordStrengthMessages } from '../PasswordStrengthMessages';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import { isPasswordValid, validateTextField } from '../../../authentication/utils/validation';
import { ResetPasswordLayoutProps } from '../../interfaces/SimplifiedAuthPageProps';
import { useAppSelector } from '../../../store/hook';
import { genericMessageHandler, isNotSpaceOrEnterKey } from '../../../common/utils/accessibilityGenericMessage';
import { WCAGStrandardStyles } from '../../../common/styles/WCAGStandardStyles';
import { DpnCheckbox } from '../DpnCheckbox';
import { DPNAcceptance } from '../../interfaces/SimplifiedAuthInputFields';

const ResetPasswordLayout = (props: ResetPasswordLayoutProps): React.JSX.Element => {
  const {
    password,
    showPassword,
    setShowPassword,
    loading,
    setPassword,
    passwordHelperText,
    setPasswordHelperText,
    changePassword,
    alert,
    errorMessageRestPasswordRef,
  } = props;
  const { t } = useTranslation();
  const { layoutCode } = useAppSelector((state) => state.template);
  const { classes } = simplifiedAuthStyles({ layoutCode });
  const { classes: WCAGClasses } = WCAGStrandardStyles();
  const companySettings = useAppSelector((state) => state.companySettings);
  const [dpnDetails, setDpnDetails] = useState<DPNAcceptance>({
    dpnAcceptance: 'false',
    dpnAcceptanceHelperText: '',
  });
  const { dpnAcceptance, dpnAcceptanceHelperText } = dpnDetails;

  /** event for handling input change and blur */
  const inputChangeHandler = (event: React.ChangeEvent<{ value: string; name: string }>): void => {
    const value = event.target.value;
    const name = event.target.name;
    const updatedValues = validateTextField(name, value);
    setPasswordHelperText(updatedValues.newPasswordHelperText);
    setPassword(updatedValues.newPassword);
  };

  const handleDpnAcceptance = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    const name = event.target.name;
    const updatedFieldValues = validateTextField(name, checked);
    setDpnDetails({ ...dpnDetails, ...updatedFieldValues });
  };

  const toggleShowPassword = useCallback((event: React.KeyboardEvent | React.MouseEvent): void => {
    if (isNotSpaceOrEnterKey(event)) {
      return;
    }
    setShowPassword((show) => !show);
  }, []);

  /** event for handling create password button click */
  const handleCreatePasswordClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();

    const updatedValues = validateTextField('newPassword', password);
    setPasswordHelperText(updatedValues.newPasswordHelperText);
    if (updatedValues.newPasswordHelperText !== '' || !isPasswordValid(password)) {
      genericMessageHandler();
      return;
    }

    const dpnAcceptanceHelperValues = validateTextField('dpnAcceptance', dpnDetails.dpnAcceptance);
    if (companySettings?.showDpn && dpnAcceptanceHelperValues.dpnAcceptanceHelperText !== '') {
      setDpnDetails({
        ...dpnDetails,
        dpnAcceptanceHelperText: dpnAcceptanceHelperValues.dpnAcceptanceHelperText,
      });
      genericMessageHandler();
      return;
    }

    changePassword();
  };

  return (
    <form aria-labelledby="resetPassword">
      <Box mb={2.5} className={`${classes.createPasswordBox}`}>
        <Typography
          role="heading"
          aria-level={1}
          id="resetPassword"
          className={`${WCAGClasses.h3} ${classes.marginBottom15}`}>
          {t('createPassword')}
        </Typography>
        <InputLabel htmlFor="newPassword" className={`${classes.newPasswordLabel} ${classes.fontWeight600}`}>
          {t('newPassword')}
        </InputLabel>
        <Box className={`${classes.marginBottom5}`}>
          <TextField
            id="newPassword"
            name="newPassword"
            margin="dense"
            required
            type={showPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            autoComplete="new-password"
            value={password}
            error={passwordHelperText !== ''}
            onChange={inputChangeHandler}
            onBlur={inputChangeHandler}
            className={`${classes.createPasswordInput}`}
            helperText={
              passwordHelperText !== '' ? (
                <span className={classes.helperText}>
                  <ErrorOutlineOutlined />
                  &nbsp;
                  {t(`${passwordHelperText}`)}
                </span>
              ) : undefined
            }
            inputProps={{
              maxLength: '100',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    data-testid="hideShowButton"
                    className={classes.pointerCursor}
                    tabIndex={0}
                    role="button"
                    onKeyDown={toggleShowPassword}
                    onClick={toggleShowPassword}
                    variant="caption">
                    {showPassword ? t('hide') : t('show')}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className={classes.marginBottom15}>
          <PasswordStrengthMessages password={password} />
        </Box>

        {/* DPN acceptance checkbox */}
        {companySettings?.showDpn && (
          <DpnCheckbox
            layoutCode={layoutCode}
            dpnAcceptance={dpnAcceptance}
            dpnAcceptanceHelperText={dpnAcceptanceHelperText}
            dataTestId="resetPasswordDpnAcceptance"
            handleDpnAcceptance={handleDpnAcceptance}
          />
        )}

        {/** alert message to show reset password api success or fails */}
        {alert !== '' && (
          <div
            ref={errorMessageRestPasswordRef}
            tabIndex={-1}
            className={`${classes.marginBottom15} ${classes.customError}`}>
            <ErrorOutlineOutlined />
            &nbsp;{t(alert)}
          </div>
        )}

        <Button
          className={`${classes.registerBtn}`}
          type="submit"
          color="primary"
          data-testid="createPasswordButton"
          variant="outlined"
          fullWidth
          disabled={loading}
          aria-hidden={loading}
          aria-disabled="false"
          onClick={handleCreatePasswordClick}>
          {t('createPassword')}
          {loading && <CircularProgress size={25} className={classes.buttonProgress} />}
        </Button>
      </Box>
    </form>
  );
};
export default ResetPasswordLayout;
